'use client';

import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import TrailingLink from '@/components/atoms/TrailingLink';
import { PageHeroSection } from '@/components/organisms/shared/PageHeroSection';
import type { BreadcrumbItem } from '@/components/molecules/Breadcrumb';
import styles from './Level1Layout.module.scss';

interface Level1LayoutProps {
  title: string;
  subtitle?: string;
  description: string; // Made required
  heroIcon?: string;
  breadcrumbs?: BreadcrumbItem[];
  children?: ReactNode;
  categories?: Array<{
    id: string;
    title: string;
    description: string;
    href: string;
    icon?: string;
  }>;
  overviewSection?: {
    title: string;
    content: ReactNode;
  };
}

// Removed unused animation variants - now using UnifiedPageHeader

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
};

export function Level1Layout({
  title,
  subtitle,
  description,
  heroIcon,
  breadcrumbs,
  children,
  categories,
  overviewSection
}: Level1LayoutProps) {
  return (
    <main className={styles.main}>
      {/* Page Hero Section */}
      <PageHeroSection
        title={title}
        description={description}
        subtitle={subtitle}
        heroIcon={heroIcon}
        breadcrumbs={breadcrumbs ?? [{ label: title }]}
        level={1}
        ctaButtons={categories && categories.length > 0 ? [
          { text: '詳細を見る', href: '#categories', variant: 'primary' },
          { text: 'お問い合わせ', href: '/contact', variant: 'secondary' }
        ] : undefined}
      />

      {/* Overview Section if provided */}
      {overviewSection && (
        <section className={styles.overview}>
          <div className={styles.container}>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className={styles.sectionTitle}>{overviewSection.title}</h2>
              {overviewSection.content}
            </motion.div>
          </div>
        </section>
      )}

      {/* Categories Grid with staggered animations */}
      {categories && categories.length > 0 && (
        <section className={styles.categories}>
          <div className={styles.container}>
            <motion.h2
              className={styles.sectionTitle}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              詳細情報
            </motion.h2>
            <motion.div
              className={styles.categoryGrid}
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {categories.map((category) => (
                <motion.div key={category.id} variants={itemVariants}>
                  <TrailingLink href={category.href} className={styles.categoryCard}>
                    {category.icon && <span className={styles.icon}>{category.icon}</span>}
                    <h3>{category.title}</h3>
                    <p>{category.description}</p>
                    <span className={styles.arrow}>→</span>
                  </TrailingLink>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Additional content */}
      {children}
    </main>
  );
}

export default Level1Layout;